import React, { useState, useEffect } from 'react';
import { MapPin, Map } from 'lucide-react';
import '../../styles/leaflet.css';

// Import Leaflet dynamiquement pour éviter les problèmes SSR
let L: any = null;
let MapContainer: any = null;
let TileLayer: any = null;
let Marker: any = null;
let useMapEvents: any = null;

interface Coordinates {
  latitude: number;
  longitude: number;
  address?: string;
}

interface LocationPickerProps {
  label: string;
  value: Coordinates | null;
  onSave: (coordinates: Coordinates) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

// Composant pour gérer les clics sur la carte
const MapClickHandler: React.FC<{ onMapClick: (lat: number, lng: number) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click: (e: any) => {
      const { lat, lng } = e.latlng;
      onMapClick(lat, lng);
    },
  });
  return null;
};

// Hook pour charger Leaflet dynamiquement
const useLeaflet = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        // Charger Leaflet et React-Leaflet
        const leafletModule = await import('leaflet');
        const reactLeafletModule = await import('react-leaflet');
        
        L = leafletModule.default;
        MapContainer = reactLeafletModule.MapContainer;
        TileLayer = reactLeafletModule.TileLayer;
        Marker = reactLeafletModule.Marker;
        useMapEvents = reactLeafletModule.useMapEvents;

        // Configuration des icônes par défaut
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        setIsLoaded(true);
      } catch (error) {
        console.error('Erreur lors du chargement de Leaflet:', error);
        setLoadError('Erreur de chargement de la carte');
      }
    };

    loadLeaflet();
  }, []);

  return { isLoaded, loadError };
};

interface Coordinates {
  latitude: number;
  longitude: number;
  address?: string;
}

interface LocationPickerProps {
  label: string;
  value: Coordinates | null;
  onSave: (coordinates: Coordinates) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  label,
  value,
  onSave,
  disabled = false,
  placeholder = "Cliquez pour sélectionner la localisation"
}) => {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [tempCoordinates, setTempCoordinates] = useState<Coordinates | null>(value);
  const { isLoaded, loadError } = useLeaflet();
  
  // Goma comme coordonnées par défaut pour la RDC
  const defaultCenter: [number, number] = [-1.6792, 29.2228];

  const handleOpenMap = () => {
    if (disabled) return;
    setIsMapOpen(true);
    if (!tempCoordinates && !value) {
      // Définir Goma comme position par défaut
      setTempCoordinates({
        latitude: defaultCenter[0],
        longitude: defaultCenter[1],
        address: "Goma, République Démocratique du Congo"
      });
    }
  };

  const handleSaveCoordinates = async () => {
    if (tempCoordinates) {
      await onSave(tempCoordinates);
      setIsMapOpen(false);
    }
  };

  const handleMapClick = async (lat: number, lng: number) => {
    // Géocodage inverse simplifié pour obtenir une adresse approximative
    let address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    
    // Ajouter des noms de villes connues de RDC
    const knownCities = [
      { lat: -1.6792, lng: 29.2228, name: "Goma" },
      { lat: -4.3317, lng: 15.3139, name: "Kinshasa" },
      { lat: -11.6792, lng: 27.4794, name: "Lubumbashi" },
      { lat: -0.5177, lng: 25.1894, name: "Kisangani" },
      { lat: -5.8667, lng: 29.2500, name: "Bukavu" },
      { lat: -2.5053, lng: 28.8642, name: "Butembo" }
    ];

    // Trouver la ville la plus proche (dans un rayon de ~50km)
    const nearestCity = knownCities.find(city => {
      const distance = Math.sqrt(
        Math.pow(city.lat - lat, 2) + Math.pow(city.lng - lng, 2)
      );
      return distance < 0.5; // Approximativement 50km
    });

    if (nearestCity) {
      address = `${nearestCity.name}, République Démocratique du Congo`;
    }

    setTempCoordinates({
      latitude: lat,
      longitude: lng,
      address: address
    });
  };

  const formatCoordinates = (coords: Coordinates) => {
    return `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
  };

  // Composant de carte interactive
  const InteractiveMap = () => {
    if (loadError) {
      return (
        <div className="w-full h-96 bg-red-50 rounded-lg flex items-center justify-center border border-red-200">
          <div className="text-center text-red-600">
            <Map className="w-12 h-12 mx-auto mb-2" />
            <p className="mb-2">Erreur de chargement de la carte</p>
            <p className="text-sm">{loadError}</p>
          </div>
        </div>
      );
    }

    if (!isLoaded || !MapContainer) {
      return (
        <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
          <div className="text-center">
            <Map className="w-12 h-12 text-gray-400 mx-auto mb-2 animate-pulse" />
            <p className="text-gray-500">Chargement de la carte...</p>
          </div>
        </div>
      );
    }

    const position: [number, number] = tempCoordinates 
      ? [tempCoordinates.latitude, tempCoordinates.longitude]
      : defaultCenter;

    return (
      <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-300">
        <MapContainer
          center={position}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          className="rounded-lg"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} />
          {tempCoordinates && (
            <Marker position={[tempCoordinates.latitude, tempCoordinates.longitude]} />
          )}
        </MapContainer>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={handleOpenMap}
          disabled={disabled}
          className={`
            w-full px-3 py-2 border border-gray-300 rounded-lg text-left
            focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]
            transition-colors duration-200
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:bg-gray-50 cursor-pointer'}
            ${value ? 'text-gray-900' : 'text-gray-400'}
          `}
        >
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span className="flex-1">
              {value ? (
                <div>
                  <div className="font-medium">{formatCoordinates(value)}</div>
                  {value.address && (
                    <div className="text-xs text-gray-500">{value.address}</div>
                  )}
                </div>
              ) : (
                placeholder
              )}
            </span>
            <Map className="w-4 h-4 text-gray-400" />
          </div>
        </button>
      </div>

      {/* Modal de carte interactive */}
      {isMapOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Sélectionner la localisation
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Cliquez sur la carte pour sélectionner un emplacement
                </p>
              </div>
              <button
                onClick={() => setIsMapOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 p-4 overflow-hidden">
              <InteractiveMap />
              
              {tempCoordinates && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Position sélectionnée
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>Coordonnées:</strong> {formatCoordinates(tempCoordinates)}
                      </p>
                      {tempCoordinates.address && (
                        <p className="text-sm text-blue-700">
                          <strong>Adresse:</strong> {tempCoordinates.address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setIsMapOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveCoordinates}
                disabled={!tempCoordinates}
                className="px-4 py-2 bg-[#197ca8] text-white rounded-md hover:bg-[#156a92] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Confirmer la sélection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};